import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  LOGIN_PORTAL_THEMES,
  LOGIN_PORTAL_DEFAULT_TEMPLATE,
  isValidPortalTemplateId
} from '../theme/loginPortalThemes';
import {
  getStoredLandingPageTemplate,
  setStoredLandingPageTemplate,
  getStoredLandingPanelsTemplate,
  setStoredLandingPanelsTemplate,
  getStoredLandingCareerTemplate,
  setStoredLandingCareerTemplate,
  getStoredLandingFeaturesTemplate,
  setStoredLandingFeaturesTemplate,
  getStoredLandingNavVariant,
  setStoredLandingNavVariant,
  syncLandingPageTemplateToPortal,
  resetLandingAppearanceToDefaults
} from '../utils/landingPagePreferences';
import { subscribeSharedPageAtmosphere } from '../utils/sharedPageAtmosphere';

const LandingPageAppearanceContext = createContext(null);

function themeFor(id) {
  return LOGIN_PORTAL_THEMES[id] || LOGIN_PORTAL_THEMES[LOGIN_PORTAL_DEFAULT_TEMPLATE];
}

function resolveNavVariant(navVariant, pageTemplateId) {
  if (navVariant === 'light' || navVariant === 'dark') return navVariant;
  return ['light', 'aqua', 'orchid'].includes(pageTemplateId) ? 'light' : 'dark';
}

export function LandingPageAppearanceProvider({ children }) {
  const [pageTemplateId, setPageTemplateIdState] = useState(() => getStoredLandingPageTemplate());
  const [panelsTemplateId, setPanelsTemplateIdState] = useState(() => getStoredLandingPanelsTemplate());
  const [careerTemplateId, setCareerTemplateIdState] = useState(() => getStoredLandingCareerTemplate());
  const [featuresTemplateId, setFeaturesTemplateIdState] = useState(() => getStoredLandingFeaturesTemplate());
  const [navVariant, setNavVariantState] = useState(() => getStoredLandingNavVariant());

  useEffect(
    () =>
      subscribeSharedPageAtmosphere((id) => {
        setPageTemplateIdState(id);
      }),
    []
  );

  const setPageTemplateId = useCallback((id) => {
    const v = isValidPortalTemplateId(id) ? id : LOGIN_PORTAL_DEFAULT_TEMPLATE;
    setPageTemplateIdState(v);
    setStoredLandingPageTemplate(v);
  }, []);

  const setPanelsTemplateId = useCallback((id) => {
    const v = isValidPortalTemplateId(id) ? id : LOGIN_PORTAL_DEFAULT_TEMPLATE;
    setPanelsTemplateIdState(v);
    setStoredLandingPanelsTemplate(v);
  }, []);

  const setCareerTemplateId = useCallback((id) => {
    const v = isValidPortalTemplateId(id) ? id : LOGIN_PORTAL_DEFAULT_TEMPLATE;
    setCareerTemplateIdState(v);
    setStoredLandingCareerTemplate(v);
  }, []);

  const setFeaturesTemplateId = useCallback((id) => {
    const v = isValidPortalTemplateId(id) ? id : LOGIN_PORTAL_DEFAULT_TEMPLATE;
    setFeaturesTemplateIdState(v);
    setStoredLandingFeaturesTemplate(v);
  }, []);

  const setNavVariant = useCallback((v) => {
    const next = v === 'light' || v === 'dark' ? v : 'auto';
    setNavVariantState(next);
    setStoredLandingNavVariant(next);
  }, []);

  const syncToQueryPortal = useCallback(() => {
    syncLandingPageTemplateToPortal(pageTemplateId);
  }, [pageTemplateId]);

  const resetAll = useCallback(() => {
    resetLandingAppearanceToDefaults();
    const def = LOGIN_PORTAL_DEFAULT_TEMPLATE;
    setPageTemplateIdState(def);
    setPanelsTemplateIdState(def);
    setCareerTemplateIdState(def);
    setFeaturesTemplateIdState(def);
    setNavVariantState('auto');
  }, []);

  const pageTheme = useMemo(() => themeFor(pageTemplateId), [pageTemplateId]);
  const panelsTheme = useMemo(() => themeFor(panelsTemplateId), [panelsTemplateId]);
  const careerTheme = useMemo(() => themeFor(careerTemplateId), [careerTemplateId]);
  const featuresTheme = useMemo(() => themeFor(featuresTemplateId), [featuresTemplateId]);
  const navBarVariant = useMemo(
    () => resolveNavVariant(navVariant, pageTemplateId),
    [navVariant, pageTemplateId]
  );

  const value = useMemo(
    () => ({
      pageTemplateId,
      setPageTemplateId,
      panelsTemplateId,
      setPanelsTemplateId,
      careerTemplateId,
      setCareerTemplateId,
      featuresTemplateId,
      setFeaturesTemplateId,
      navVariant,
      setNavVariant,
      navBarVariant,
      pageTheme,
      panelsTheme,
      careerTheme,
      featuresTheme,
      syncToQueryPortal,
      resetAll
    }),
    [
      pageTemplateId,
      setPageTemplateId,
      panelsTemplateId,
      setPanelsTemplateId,
      careerTemplateId,
      setCareerTemplateId,
      featuresTemplateId,
      setFeaturesTemplateId,
      navVariant,
      setNavVariant,
      navBarVariant,
      pageTheme,
      panelsTheme,
      careerTheme,
      featuresTheme,
      syncToQueryPortal,
      resetAll
    ]
  );

  return (
    <LandingPageAppearanceContext.Provider value={value}>{children}</LandingPageAppearanceContext.Provider>
  );
}

export function useLandingPageAppearance() {
  const ctx = useContext(LandingPageAppearanceContext);
  if (!ctx) throw new Error('useLandingPageAppearance must be used within LandingPageAppearanceProvider');
  return ctx;
}
